import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@immo/ui-kit';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { BreadcrumbLeading } from '@/components/layout/page-leading';
import { TagIcon } from '@/components/layout/icons';
import {
  AGENT_SKILLS_PATH,
  AgentSkillForm,
  EMPTY_FORM,
  type AgentSkillFormState,
} from '@/pages/skills/agent-skill-form';

function errorCodeOf(body: unknown): string | undefined {
  return (body as { error?: { code?: string } } | undefined)?.error?.code;
}

export function NewAgentSkillPage() {
  const t = useTranslations('pages.admin.skills.agent-skills.editor');
  const tPage = useTranslations('pages.admin.skills.agent-skills');
  const tErrors = useTranslations('errors');
  const navigate = useNavigate();

  const [form, setForm] = useState<AgentSkillFormState>(EMPTY_FORM);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setInlineError(null);
    setBusy(true);

    try {
      const result = await apiClient.adminAgentSkills.create({
        body: {
          key: form.key,
          name: form.name,
          description: form.description,
          instructions: form.instructions,
          supportedTriggers: [...form.supportedTriggers],
          defaultAllowlist: [...form.defaultAllowlist],
          approvalWindowDays: form.approvalWindowDays,
        },
      });

      if (result.ok) {
        navigate(`${AGENT_SKILLS_PATH}/${result.body.key}`);

        return;
      }

      // A duplicate key or an invalid field is a permanent refusal carrying its
      // own code — render that, never a generic "try again" (BL-020).
      const code = errorCodeOf(result.body);
      setInlineError(code !== undefined ? tErrors(code) : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageTopbar
        leading={
          <BreadcrumbLeading
            icon={TagIcon}
            crumbs={[{ label: tPage('title'), to: AGENT_SKILLS_PATH }, { label: t('create-title') }]}
          />
        }
      />
      <div className="mx-auto max-w-3xl space-y-4 p-6">
        <h1 className="text-lg font-semibold text-text-primary">{t('create-title')}</h1>

        <AgentSkillForm value={form} onChange={setForm} keyEditable disabled={busy} error={inlineError} />

        <div className="flex items-center gap-2">
          <Button type="button" onClick={create} disabled={busy}>
            {t('create')}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate(AGENT_SKILLS_PATH)} disabled={busy}>
            {t('cancel')}
          </Button>
        </div>
      </div>
    </>
  );
}
